import React, { useState } from "react";
import usePageTitle from "../../../services/usePageTitle";
import usePageState from "../../../hooks/usePageState";
import useIsRoleUser from "../../../hooks/useIsRoleUser";
import PageLayout from "../../../components/PageLayout";
import SearchToolbar from "../../../components/SearchToolbar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import UsersTable from "./UsersTable";
import { AddUserDialog, UpdateUserDialog } from "./UserDialog";
import UserPriviligesDialog from "./UserPriviligesDialog";
import {
  fetchUsers,
  updateUser,
  deleteUser,
  activateModule,
  deactivateModule,
  assignUserRole,
} from "./UsersAPI";
import { extractMsg } from "../../../utils/errorUtils";
import usersHelp from "../../../content/help/users.json";

export default function Users({ pageTitle = "Users" }) {
  usePageTitle("SkeyeGate Users - SkeyeGate™");
  const isRoleUser = useIsRoleUser();

  const {
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    onPageChange,
  } = usePageState({
    fetchFn: fetchUsers,
    filterFields: ["username", "email", "id"],
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [privilegeModalOpen, setPrivilegeModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleAddUser = async (values) => {
    try {
      const { addUser } = await import("./UsersAPI");
      await addUser({ ...values, roles: [values.roles] });
      messageApi.success("User added successfully");
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to create user"),
        key: "add-user-error",
      });
    }
  };

  const handleUpdateUser = async (userId, formValues) => {
    try {
      // 1. Update basic info (username / email)
      const userData = {
        username: formValues.username,
        email: formValues.email,
      };
      if (formValues.password) userData.password = formValues.password;
      await updateUser(userId, userData);

      const oldRole = selectedUser?.roles?.[0]?.name;
      const newRole = formValues.role;

      if (oldRole !== newRole) {
        const cleanRoleName = newRole.replace("ROLE_", "");
        await assignUserRole(userId, cleanRoleName);
      }

      messageApi.success("User updated successfully");
      setUpdateModalOpen(false);
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to update user"),
        key: "update-user-error",
      });
      console.error(error.response?.data?.message || "Failed to update user");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);

      messageApi.success("User deleted successfully");
      setDeleteModalOpen(false);
      loadData();
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to delete user"),
        key: "delete-user-error",
      });
      console.error("Failed to delete user", error);
    }
  };

  const handleSavePrivileges = async (selectedModuleLabels) => {
    if (!selectedUser) return;
    try {
      const originalModules = Array.isArray(selectedUser.activatedModules)
        ? selectedUser.activatedModules.map((m) => m.label)
        : [];
      const toAdd = selectedModuleLabels.filter(
        (m) => !originalModules.includes(m),
      );
      const toRemove = originalModules.filter(
        (m) => !selectedModuleLabels.includes(m),
      );
      for (const moduleLabel of toAdd)
        await activateModule(selectedUser.id, moduleLabel);
      for (const moduleLabel of toRemove)
        await deactivateModule(selectedUser.id, moduleLabel);
      messageApi.success("Privileges updated successfully");
      loadData();
    } catch (error) {
      console.error("Failed to update privileges", error);
      throw error;
    }
  };

  return (
    <div className="users-page">
      {contextHolder}
      <PageLayout
        title={pageTitle}
        description="Manage Users, view details, and monitor activity in real time."
        helpContent={usersHelp}
      >
        <SearchToolbar
          search={searchText}
          onSearchChange={handleSearch}
          onClearFilter={clearFilter}
          addButtonLabel="Add User"
          onAdd={() => setAddModalOpen(true)}
          showAdd={!isRoleUser}
        />
        <UsersTable
          data={filteredData}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={onPageChange}
          openUpdateDialog={(user) => {
            setSelectedUser(user);
            setUpdateModalOpen(true);
          }}
          openDeleteDialog={(user) => {
            setSelectedUser(user);
            setDeleteModalOpen(true);
          }}
          openPrivilegesDialog={(user) => {
            setSelectedUser(user);
            setPrivilegeModalOpen(true);
          }}
        />
      </PageLayout>
      <AddUserDialog
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUser}
      />
      <UpdateUserDialog
        open={updateModalOpen}
        user={selectedUser}
        onClose={() => setUpdateModalOpen(false)}
        onSave={handleUpdateUser}
      />
      <ConfirmDeleteModal
        open={deleteModalOpen}
        entityName="User"
        detailFields={
          selectedUser
            ? [
                { label: "Username", value: selectedUser.username },
                { label: "Email", value: selectedUser.email },
              ]
            : []
        }
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => handleDeleteUser(selectedUser.id)}
      />
      <UserPriviligesDialog
        open={privilegeModalOpen}
        user={selectedUser}
        onClose={() => setPrivilegeModalOpen(false)}
        onSave={handleSavePrivileges}
      />
    </div>
  );
}
